// Thin fetch wrapper for the Cerberus API.
//
// Base URL is the HttpApi v2 stage endpoint published by the
// CerberusStack. The deployed UI consumes the prod endpoint; tests
// can override via `setApiBaseUrl`.

import type {
  ApiError,
  LoginRequest,
  LoginResponse,
  MfaChallengeRequest,
  MfaEnrollResponse,
  MfaVerifyRequest,
  PasswordResetConfirmBody,
  PasswordResetRequestBody,
  SignupRequest,
  SignupResponse,
  TokenPair,
  VerifyResponse,
} from "./types";

const DEFAULT_BASE = "https://ik8729j7e1.execute-api.us-east-1.amazonaws.com";
let baseUrl = DEFAULT_BASE;

export function setApiBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

export function getApiBaseUrl(): string {
  return baseUrl;
}

export class CerberusApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export function errorMessage(e: unknown, fallback: string): string {
  return e instanceof CerberusApiError ? e.message : fallback;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  bearer?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
  };
  if (bearer) headers.authorization = `Bearer ${bearer}`;

  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = (json ?? {}) as Partial<ApiError>;
    const payload = err.error;
    const message =
      typeof payload === "string"
        ? payload
        : payload?.message ?? res.statusText ?? "request failed";
    const code =
      typeof payload === "object" && payload !== null
        ? payload.code
        : err.code;
    throw new CerberusApiError(res.status, message, code);
  }
  return json as T;
}

export const cerberus = {
  signup(body: SignupRequest): Promise<SignupResponse> {
    return request("POST", "/auth/signup", {
      ...body,
      email: body.email.toLowerCase().trim(),
    });
  },
  verify(token: string): Promise<VerifyResponse> {
    return request("GET", `/auth/verify?token=${encodeURIComponent(token)}`);
  },
  login(body: LoginRequest): Promise<LoginResponse> {
    return request("POST", "/auth/login", {
      ...body,
      email: body.email.toLowerCase().trim(),
    });
  },
  mfaChallenge(body: MfaChallengeRequest): Promise<TokenPair> {
    return request("POST", "/auth/login/mfa-challenge", body);
  },
  passwordResetRequest(body: PasswordResetRequestBody): Promise<{ status: string }> {
    return request("POST", "/auth/password-reset/request", {
      email: body.email.toLowerCase().trim(),
    });
  },
  passwordResetConfirm(body: PasswordResetConfirmBody): Promise<{ status: string }> {
    return request("POST", "/auth/password-reset/confirm", body);
  },
  mfaEnroll(bearer: string): Promise<MfaEnrollResponse> {
    return request("POST", "/auth/mfa/enroll", {}, bearer);
  },
  mfaVerify(bearer: string, body: MfaVerifyRequest): Promise<{ mfa_enabled: true }> {
    return request("POST", "/auth/mfa/verify", body, bearer);
  },
};
